import type { StyleRule } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import {
    DUO_COLUMN_WIDTH,
    ROW_GAP_MOBILE,
    COLUMN_WIDTH_100,
} from '@modules/page-builder/config/column-constants';

export const DuoImageModuleContainer: StyleRule = ({
    theme,
    isDuoPortrait,
}) => ({
    display: 'flex',
    flexFlow: 'column',

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        width: '100%',
        columnGap: pxToRem(40),
        gridTemplateRows: `1fr`,
        maxWidth: isDuoPortrait
            ? pxToRem(COLUMN_WIDTH_100)
            : pxToRem(DUO_COLUMN_WIDTH + 100),
        margin: '0 auto',
    },
});

/**
 * Width constraints for each image within a DuoImageModule.
 * Aspect ratio is delegated to DetailSingleImageContainer which reads
 * the original dimensions directly from the column data.
 */
export const DuoImageContainerImage: (
    isSmall: boolean,
    isPortrait?: boolean,
) => StyleRule<{
    isDuoPortrait?: boolean;
}> =
    (isSmall, isPortrait) =>
    ({ isDuoPortrait }) => {
        if (isSmall) {
            // Cap small duo images at 500px; aspect ratio is preserved by the image shell
            return {
                margin: 'auto',
                width: pxToRem(500),
                height: 'auto',
            };
        }

        if (!isPortrait) {
            // Cap landscape duo images at 750px; aspect ratio is preserved by the image shell
            return {
                margin: '0',
                width: pxToRem(750),
                height: 'auto',
            };
        }

        // Portrait images: use full width when both images are portrait, otherwise cap at 750px
        return {
            margin: '0',
            width: isDuoPortrait ? '100%' : pxToRem(750),
            height: 'auto',
        };
    };

export const DuoImageModuleContainerPageBuilder: StyleRule = ({ theme }) => ({
    display: 'flex',
    flexFlow: 'column',

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: pxToRem(204),
        gridTemplateRows: `1fr`,
        maxWidth: pxToRem(DUO_COLUMN_WIDTH),
        margin: 'auto',
    },
});

export const DuoImageContainerImagePageBuilder: (
    isMedium: boolean,
) => StyleRule<{
    columns: number;
    mobile: boolean;
}> =
    (isMedium) =>
    ({ columns, mobile, theme }) => {
        if (isMedium && !mobile) {
            return { margin: '0 auto' };
        }

        if (mobile) {
            return Object.assign({
                margin: '0 auto',
                '&:last-child':
                    columns > 1
                        ? {
                              margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
                          }
                        : undefined,
            });
        }
        return {
            margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
            [theme.screen.laptop]: {
                height: 'auto !important',
                margin: 'auto',
                display: 'flex',
                alignItems: 'start',
                justifyContent: 'center',
                minHeight: '100%',
                '&:has(.isMedium)': {
                    margin: '0 auto',
                },
            },
        };
    };
